/* ===================================================================
   product-form.js

   FIXES vs. the original version:

   1. RACE: SUBMIT LISTENER  (was: "Add to Cart goes to the /cart page")
      The listener used to be attached inside setTimeout(..., 100). For
      those 100ms the form was an ordinary HTML form, so a click did a
      native POST to /cart/add and Shopify replied with a redirect to
      /cart. It is now bound synchronously in connectedCallback.

   2. RACE: CART ELEMENT NOT YET UPGRADED  (was: "nothing opens")
      <cart-drawer> can be in the DOM long before cart-drawer.js runs. Until
      it is upgraded it is a plain HTMLElement with no renderContents() or
      getSectionsToRender(). The old code either skipped the sections
      request or threw into the .catch, so the item was added but the drawer
      never appeared. We now await customElements.whenDefined() before
      building the request.

   3. MISSING GLOBAL DEPENDENCIES
      fetchConfig, routes, publish, PUB_SUB_EVENTS and CartPerformance all
      come from other deferred scripts and may not exist yet. Each is now
      accessed through a guarded helper with a working fallback, so a
      not-yet-loaded dependency degrades instead of aborting the submit.

   4. UNGUARDED DOM ACCESS  (was: "button does nothing at all")
      onSubmitHandler called preventDefault() and then dereferenced
      this.submitButton and .loading__spinner with no null checks, throwing
      after the default was already cancelled. Both are now lazy + guarded.
=================================================================== */

customElements.get("product-form") ||
  customElements.define(
    "product-form",
    class extends HTMLElement {
      constructor() {
        super();
        this.originalButtonText = "";
        this.persistentTextActive = false;
      }

      isSoldOut() {
        const button = this.getSubmitButton();
        return button && button.classList.contains("sold-out");
      }

      /* ---------------------------------------------------------------
         Lazy resolvers. Re-query when we don't already have a reference,
         so elements that appear after this component connects (cart
         drawer rendered by a later section, button swapped by a variant
         change, etc.) are still found.
      --------------------------------------------------------------- */
      getForm() {
        if (!this.form || !this.contains(this.form)) {
          this.form = this.querySelector("form");
        }
        return this.form;
      }

      getSubmitButton() {
        if (!this.submitButton || !this.contains(this.submitButton)) {
          this.submitButton = this.querySelector('[type="submit"]');
        }
        return this.submitButton;
      }

      getCart() {
        if (!this.cart || !document.contains(this.cart)) {
          this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        }
        return this.cart;
      }

      /* FIX 2: the element existing is not the same as the element working.
         A custom element sitting in the DOM before its defining script has
         run is an inert HTMLElement — querySelector finds it, but none of
         its methods exist yet. Wait for the upgrade before using it. */
      /* Timeout lowered from 6000ms. This is awaited BEFORE the add request
         is sent (the sections must be asked for in the same call), so a slow
         or missing cart drawer directly delays the shopper's add to cart.
         If the drawer has not upgraded within 2s of a click, something is
         wrong and it is better to add the item immediately and render the
         drawer late than to sit on the request. */
      async getUpgradedCart(timeout = 2000) {
        const cart = this.getCart();
        if (!cart) return null;
        if (typeof cart.renderContents === "function") return cart;

        const tagName = cart.tagName.toLowerCase();
        if (window.customElements && typeof customElements.whenDefined === "function") {
          await Promise.race([
            customElements.whenDefined(tagName),
            new Promise((resolve) => setTimeout(resolve, timeout)),
          ]);
        }

        // Re-read: the upgrade replaces the prototype on the same node.
        const upgraded = this.getCart();
        return upgraded && typeof upgraded.renderContents === "function" ? upgraded : null;
      }

      /* FIX 3: every one of these lives in a separate deferred script.
         Fall back rather than throw if one hasn't loaded yet. */
      buildFetchConfig() {
        if (typeof fetchConfig === "function") {
          const config = fetchConfig("javascript");
          config.headers["X-Requested-With"] = "XMLHttpRequest";
          delete config.headers["Content-Type"];
          return config;
        }
        return {
          method: "POST",
          headers: {
            Accept: "application/javascript",
            "X-Requested-With": "XMLHttpRequest",
          },
        };
      }

      getCartAddUrl() {
        return (window.routes && window.routes.cart_add_url) || "/cart/add";
      }

      getCartUrl() {
        return (window.routes && window.routes.cart_url) || "/cart";
      }

      safePublish(eventKey, payload) {
        try {
          if (typeof publish !== "function" || typeof PUB_SUB_EVENTS === "undefined") return null;
          const eventName = PUB_SUB_EVENTS[eventKey];
          if (!eventName) return null;
          return publish(eventName, payload);
        } catch (error) {
          console.warn("product-form.js: publish failed:", error);
          return null;
        }
      }

      safeMeasure(name, callback) {
        try {
          if (typeof CartPerformance !== "undefined" && typeof CartPerformance.measure === "function") {
            CartPerformance.measure(name, callback);
            return;
          }
        } catch (error) {
          console.warn("product-form.js: CartPerformance.measure failed:", error);
        }
        // The measurement is telemetry; the callback is the actual work.
        callback();
      }

      getSpinner() {
        return this.querySelector(".loading__spinner");
      }

      connectedCallback() {
        // FIX 1: bind the submit handler NOW, not in 100ms. This is the
        // single most important line in the file — every millisecond it is
        // not bound is a millisecond where a click native-submits to /cart.
        const form = this.getForm();
        if (form && !form.dataset.submitBound) {
          form.dataset.submitBound = "true";
          form.addEventListener("submit", this.onSubmitHandler.bind(this));
        }

        const variantIdInput = this.variantIdInput;
        if (variantIdInput) variantIdInput.disabled = false;

        // Warm the cart reference, but never rely on it being non-null here.
        this.getCart();

        const submitButton = this.getSubmitButton();
        if (submitButton && document.querySelector("cart-drawer")) {
          submitButton.setAttribute("aria-haspopup", "dialog");
        }
        this.hideErrors = "true" === this.dataset.hideErrors;

        // Cosmetic setup (button label text, observers) can safely wait —
        // it does not affect whether the form submits correctly.
        setTimeout(() => this.setupButtonText(), 100);

        document.addEventListener("variant:change", this.handleVariantChange.bind(this));
      }

      setupButtonText() {
        const submitButton = this.getSubmitButton();
        if (!submitButton) return;

        this.submitButtonText = submitButton.querySelector(".button-text");
        if (this.submitButtonText) {
          this.originalButtonText = this.submitButtonText.textContent;
          submitButton.dataset.originalText = this.originalButtonText;
        }

        const wantsPersistent =
          "true" === submitButton.dataset.textOverride || submitButton.classList.contains("has-persistent-text");

        if (wantsPersistent && this.submitButtonText) {
          this.persistentTextActive = true;
          if (!this.isSoldOut()) {
            const showPrice = "true" === submitButton.dataset.showPrice;
            const persistent = submitButton.dataset.persistentText;
            if (persistent) {
              if (!showPrice || !this.submitButtonText.textContent.trim().startsWith(persistent.trim())) {
                this.submitButtonText.textContent = persistent;
              }
              localStorage.setItem("addToCartButtonText", persistent);
            } else {
              const stored = localStorage.getItem("addToCartButtonText");
              if (stored) {
                if (!showPrice || !this.submitButtonText.textContent.trim().startsWith(stored.trim())) {
                  this.submitButtonText.textContent = stored;
                }
                submitButton.dataset.persistentText = stored;
              }
            }
          }
        }

        if ((this.persistentTextActive || "true" === submitButton.dataset.textOverride) && this.submitButtonText) {
          this.setupButtonTextObserver();
        }
      }

      setupButtonTextObserver() {
        if (!this.submitButtonText || !this.persistentTextActive) return;
        const submitButton = this.getSubmitButton();
        if (!submitButton) return;

        const persistent = submitButton.dataset.persistentText || localStorage.getItem("addToCartButtonText");
        if (!persistent) return;

        const showPrice = "true" === submitButton.dataset.showPrice;
        const config = { characterData: true, childList: true, subtree: true };

        this.buttonTextObserver = new MutationObserver(() => {
          if (this.isSoldOut()) return;
          const currentText = this.submitButtonText.textContent.trim();
          const persistentText = persistent.trim();
          if (showPrice) {
            if (!currentText.startsWith(persistentText)) this.submitButtonText.textContent = persistent;
          } else if (currentText !== persistentText) {
            this.submitButtonText.textContent = persistent;
          }
        });
        this.buttonTextObserver.observe(this.submitButtonText, config);
      }

      handleVariantChange() {
        if (!this.submitButtonText) return;
        setTimeout(() => {
          if (this.isSoldOut() || !this.persistentTextActive) return;
          const submitButton = this.getSubmitButton();
          if (!submitButton) return;
          if ("true" === submitButton.dataset.showPrice) return;
          const persistent = submitButton.dataset.persistentText || localStorage.getItem("addToCartButtonText");
          if (persistent) this.submitButtonText.textContent = persistent;
        }, 50);
      }

      ensureSellingPlanValue() {
        let planIdToUse = null;
        try {
          const form = this.getForm();
          if (!form) return null;
          const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
          if (!sellingPlanInput) return null;

          if (sellingPlanInput.value) return sellingPlanInput.value;

          const subscribeToggle = document.querySelector(
            '.subscribe-save-wrapper input[type="checkbox"]:checked, .subscribe-save-wrapper-dynamic input[type="checkbox"]:checked',
          );
          if (subscribeToggle) {
            const wrapper = subscribeToggle.closest(".subscribe-save-wrapper, .subscribe-save-wrapper-dynamic");
            if (wrapper) planIdToUse = wrapper.dataset.sellingPlanId || subscribeToggle.dataset.planId;
            if (planIdToUse) {
              sellingPlanInput.value = planIdToUse;
              sellingPlanInput.setAttribute("value", planIdToUse);
            }
          }
        } catch (error) {
          console.warn("product-form.js: error setting selling_plan:", error);
        }
        return planIdToUse;
      }

      async onSubmitHandler(event) {
        const form = this.getForm();

        // FIX 4: if we cannot drive the request ourselves, do NOT cancel the
        // default. Letting the browser submit natively is a worse experience
        // than the drawer, but it is far better than a dead button.
        if (!form) return;

        event.preventDefault();

        const submitButton = this.getSubmitButton();
        if (submitButton && "true" === submitButton.getAttribute("aria-disabled")) return;

        this.handleErrorMessage();

        if (submitButton) {
          submitButton.setAttribute("aria-disabled", true);
          submitButton.classList.add("loading");
        }
        const spinner = this.getSpinner();
        if (spinner) spinner.classList.remove("hidden");

        try {
          const config = this.buildFetchConfig();

          this.ensureSellingPlanValue();

          const formData = new FormData(form);

          // FIX 2: wait here, before the request is built. The sections must
          // be requested in the SAME call that adds the item — asking after
          // the fact would mean a second round trip and a visible flash.
          const cart = await this.getUpgradedCart();

          if (cart && typeof cart.getSectionsToRender === "function") {
            formData.append(
              "sections",
              cart.getSectionsToRender().map((section) => section.id),
            );
            formData.append("sections_url", window.location.pathname);
            if (typeof cart.setActiveElement === "function") {
              cart.setActiveElement(document.activeElement);
            }
          }

          config.body = formData;

          const response = await fetch(this.getCartAddUrl(), config);
          const responseData = await response.json();
          await this.handleCartResponse(formData, event, responseData, cart);
        } catch (error) {
          console.error("product-form.js: add to cart failed:", error);
        } finally {
          this.handleSubmitFinally(event);
        }
      }

      async handleCartResponse(formData, event, responseData, knownCart) {
        const submitButton = this.getSubmitButton();

        if (responseData.status) {
          this.safePublish("cartError", {
            source: "product-form",
            productVariantId: formData.get("id"),
            errors: responseData.errors || responseData.description,
            message: responseData.message,
          });
          this.handleErrorMessage(responseData.description);

          const soldOutMessage = submitButton && submitButton.querySelector(".sold-out-message");
          if (!soldOutMessage) return;
          submitButton.setAttribute("aria-disabled", true);
          if (this.submitButtonText) this.submitButtonText.classList.add("hidden");
          soldOutMessage.classList.remove("hidden");
          this.error = true;
          return;
        }

        // Prefer the element we already waited for; only re-await if the
        // request was built before it finished upgrading.
        const cart = knownCart && typeof knownCart.renderContents === "function"
          ? knownCart
          : await this.getUpgradedCart();

        if (!cart) {
          // Genuinely no usable drawer or notification on this page — the
          // redirect is the correct last resort, not a symptom of a race.
          window.location = this.getCartUrl();
          return;
        }

        let marker = null;
        try {
          if (typeof CartPerformance !== "undefined" && typeof CartPerformance.createStartingMarker === "function") {
            marker = CartPerformance.createStartingMarker("add:wait-for-subscribers");
          }
        } catch (error) {
          marker = null;
        }

        if (!this.error) {
          const published = this.safePublish("cartUpdate", {
            source: "product-form",
            productVariantId: formData.get("id"),
            cartData: responseData,
          });
          if (published && typeof published.then === "function") {
            published.then(() => {
              try {
                if (marker && typeof CartPerformance.measureFromMarker === "function") {
                  CartPerformance.measureFromMarker("add:wait-for-subscribers", marker);
                }
              } catch (error) {
                /* telemetry only */
              }
            });
          }
        }
        this.error = false;

        const quickAddModal = this.closest("quick-add-modal");
        if (quickAddModal) {
          document.body.addEventListener(
            "modalClosed",
            () => {
              setTimeout(() => {
                this.safeMeasure("add:paint-updated-sections", () => {
                  cart.renderContents(responseData);
                  this.ensureCartVisible(cart);
                });
              });
            },
            { once: true },
          );
          quickAddModal.hide(true);
        } else {
          this.safeMeasure("add:paint-updated-sections", () => {
            cart.renderContents(responseData);
            this.ensureCartVisible(cart);
          });
        }
      }

      /* Safety net: renderContents() normally opens the drawer itself, but
         if the sections payload was missing (because the element upgraded
         only after the request went out) it can render without opening.
         Opening an already-open drawer is a no-op, so this is safe. */
      ensureCartVisible(cart) {
        if (!cart || cart.tagName.toLowerCase() !== "cart-drawer") return;
        setTimeout(() => {
          const isOpen = cart.classList.contains("active") || cart.classList.contains("is-open");
          if (!isOpen && typeof cart.open === "function") {
            try {
              cart.open();
            } catch (error) {
              console.warn("product-form.js: could not open cart drawer:", error);
            }
          }
        }, 0);
      }

      handleSubmitFinally(event) {
        const submitButton = this.getSubmitButton();
        const cart = this.cart;

        if (submitButton) submitButton.classList.remove("loading");
        if (cart && cart.classList.contains("is-empty")) cart.classList.remove("is-empty");
        if (!this.error && submitButton) submitButton.removeAttribute("aria-disabled");

        const spinner = this.getSpinner();
        if (spinner) spinner.classList.add("hidden");

        if (
          !this.error &&
          submitButton &&
          "true" !== submitButton.dataset.textOverride &&
          this.persistentTextActive &&
          !this.isSoldOut()
        ) {
          const showPrice = "true" === submitButton.dataset.showPrice;
          const persistent = submitButton.dataset.persistentText || localStorage.getItem("addToCartButtonText");
          if (persistent && this.submitButtonText) {
            if (!showPrice || !this.submitButtonText.textContent.trim().startsWith(persistent.trim())) {
              this.submitButtonText.textContent = persistent;
            }
          }
        }

        try {
          if (typeof CartPerformance !== "undefined" && typeof CartPerformance.measureFromEvent === "function") {
            CartPerformance.measureFromEvent("add:user-action", event);
          }
        } catch (error) {
          /* telemetry only */
        }
      }

      handleErrorMessage(message = false) {
        if (this.hideErrors) return;
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector(".product-form__error-message");
        this.errorMessageWrapper.toggleAttribute("hidden", !message);
        if (message && this.errorMessage) this.errorMessage.textContent = message;
      }

      toggleSubmitButton(disable = true, text) {
        const submitButton = this.getSubmitButton();
        if (!submitButton) return;

        if (disable) {
          submitButton.setAttribute("disabled", "disabled");
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
          return;
        }

        submitButton.removeAttribute("disabled");
        if ("true" === submitButton.dataset.textOverride) return;
        if (!this.submitButtonText) return;

        if (this.persistentTextActive) {
          const persistent = submitButton.dataset.persistentText || localStorage.getItem("addToCartButtonText");
          if (persistent) {
            this.submitButtonText.textContent = persistent;
            return;
          }
        }

        if (this.originalButtonText) {
          this.submitButtonText.textContent = this.originalButtonText;
        } else if (submitButton.dataset.originalText) {
          this.submitButtonText.textContent = submitButton.dataset.originalText;
        } else if (window.variantStrings && window.variantStrings.addToCart) {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      disconnectedCallback() {
        if (this.buttonTextObserver) this.buttonTextObserver.disconnect();
        document.removeEventListener("variant:change", this.handleVariantChange);
      }

      get variantIdInput() {
        const form = this.getForm();
        return form ? form.querySelector("[name=id]") : null;
      }
    },
  );
